import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ToggleVisualAid,
  ClientMenuLivePreview,
} from "../../AppsMenu/AppProfile/SettingsVisualAid";

const ACCENT = "#fa8072";

export const DEFAULT_RESTAURANT = {
  menuMode: "view",
  serviceType: "Self Service",
  clientMenuShowLogo: true,
  clientWelcomeScreenEnabled: true,
  clientWelcomeVideoEnabled: true,
  clientShowAllMenusOnOnePage: false,
  clientShowClaimButton: true,
  clientMenuVoiceEnabled: false,
  clientShowWelcomeContactButton: true,
  clientShowStorytellingButton: true,
  clientShowEventsButton: true,
  clientShowReservationButton: true,
};

export const DEFAULT_MENU_DISPLAY = {
  productImagesDisabled: false,
  productPricesHidden: false,
  searchBarHidden: false,
  summaryButtonEnabled: false,
};

function ToggleRow({
  id,
  title,
  hint,
  kind,
  on,
  onChange,
  previewPage,
  onPreviewPage,
  disabled = false,
}) {
  return (
    <div
      className={`start-visual-toggle${disabled ? " is-disabled" : ""}`}
      data-preview-page={previewPage}
      onPointerDown={() => {
        if (!disabled) onPreviewPage?.(previewPage);
      }}
    >
      <div className="start-visual-toggle__copy">
        <label className="start-visual-toggle__title" htmlFor={id}>
          {title}
        </label>
        {hint ? <p className="start-visual-toggle__hint">{hint}</p> : null}
      </div>
      <ToggleVisualAid kind={kind} on={on} accent={ACCENT} />
      <div className="form-check form-switch m-0">
        <input
          id={id}
          className="form-check-input"
          type="checkbox"
          checked={on}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      </div>
    </div>
  );
}

export default function WizardVisualSettings({ draft, setDraft, hideIntro = false }) {
  const { t } = useTranslation();
  const [livePreviewPage, setLivePreviewPage] = useState("welcome");
  const isCustom = draft?.visualSettingsMode === "custom";
  const restaurant = isCustom
    ? { ...DEFAULT_RESTAURANT, ...(draft?.restaurant || {}) }
    : { ...DEFAULT_RESTAURANT };
  const menu = isCustom ? draft?.menu || {} : { ...DEFAULT_MENU_DISPLAY };

  const setMode = (mode) => {
    setDraft((prev) => {
      if (mode === "recommended") {
        return {
          ...prev,
          visualSettingsMode: "recommended",
          restaurant: { ...DEFAULT_RESTAURANT },
          menu: { ...prev.menu, ...DEFAULT_MENU_DISPLAY },
        };
      }
      return {
        ...prev,
        visualSettingsMode: "custom",
        restaurant: { ...DEFAULT_RESTAURANT, ...(prev.restaurant || {}) },
      };
    });
  };

  const patchRestaurant = (next) => {
    if (!isCustom) return;
    setDraft((prev) => ({
      ...prev,
      restaurant: { ...DEFAULT_RESTAURANT, ...(prev.restaurant || {}), ...next },
    }));
  };

  const patchMenu = (next) => {
    if (!isCustom) return;
    setDraft((prev) => ({
      ...prev,
      menu: { ...prev.menu, ...next },
    }));
  };

  const imagesShown = menu.productImagesDisabled !== true;
  const pricesShown = menu.productPricesHidden !== true;
  const searchShown = menu.searchBarHidden !== true;
  const summaryEnabled = menu.summaryButtonEnabled === true;

  return (
    <div className="start-visual-settings">
      {hideIntro ? (
        <p className="start-step1-extra__hint">{t("wizardVisualPresetHint")}</p>
      ) : (
        <>
          <div className="start-step1-panel__header">
            <span className="start-step1-panel__eyebrow">{t("wizardVisualPresetLabel")}</span>
          </div>
          <p className="start-step1-panel__hint">{t("wizardVisualPresetHint")}</p>
        </>
      )}

      <div className="start-visual-presets" role="radiogroup" aria-label={t("wizardVisualPresetLabel")}>
        <button
          type="button"
          role="radio"
          aria-checked={!isCustom}
          className={`start-visual-preset${!isCustom ? " is-selected" : ""}`}
          onClick={() => setMode("recommended")}
        >
          <strong>{t("wizardVisualPresetRecommended")}</strong>
          <span>{t("wizardVisualPresetRecommendedHint")}</span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={isCustom}
          className={`start-visual-preset${isCustom ? " is-selected" : ""}`}
          onClick={() => setMode("custom")}
        >
          <strong>{t("wizardVisualPresetCustom")}</strong>
          <span>{t("wizardVisualPresetCustomHint")}</span>
        </button>
      </div>

      {isCustom ? (
        <div className="sva-live start-visual-live">
          <div className="start-visual-toggles">
            <ToggleRow
              id="wizard-clientMenuShowLogo"
              title={t("clientMenuShowLogo")}
              kind="logo"
              previewPage="menu"
              onPreviewPage={setLivePreviewPage}
              on={restaurant.clientMenuShowLogo !== false}
              onChange={(clientMenuShowLogo) => patchRestaurant({ clientMenuShowLogo })}
            />
            <ToggleRow
              id="wizard-clientWelcomeScreenEnabled"
              title={t("clientWelcomeScreenEnabled")}
              hint={t("visualAidWelcomePageHint")}
              kind="welcome"
              previewPage="welcome"
              onPreviewPage={setLivePreviewPage}
              on={restaurant.clientWelcomeScreenEnabled !== false}
              onChange={(clientWelcomeScreenEnabled) =>
                patchRestaurant({ clientWelcomeScreenEnabled })
              }
            />
            <ToggleRow
              id="wizard-clientWelcomeVideoEnabled"
              title={t("clientWelcomeVideoEnabled")}
              hint={t("visualAidVideoFirstPageHint")}
              kind="video"
              previewPage="welcome"
              onPreviewPage={setLivePreviewPage}
              on={restaurant.clientWelcomeVideoEnabled !== false}
              onChange={(clientWelcomeVideoEnabled) =>
                patchRestaurant({ clientWelcomeVideoEnabled })
              }
            />
            <ToggleRow
              id="wizard-clientShowAllMenusOnOnePage"
              title={t("clientShowAllMenusOnOnePage")}
              kind="onePage"
              previewPage="menu"
              onPreviewPage={setLivePreviewPage}
              on={restaurant.clientShowAllMenusOnOnePage === true}
              onChange={(clientShowAllMenusOnOnePage) =>
                patchRestaurant({ clientShowAllMenusOnOnePage })
              }
            />
            <ToggleRow
              id="wizard-clientShowClaimButton"
              title={t("clientShowClaimButton")}
              kind="claim"
              previewPage="menu"
              onPreviewPage={setLivePreviewPage}
              on={restaurant.clientShowClaimButton !== false}
              onChange={(clientShowClaimButton) =>
                patchRestaurant({ clientShowClaimButton })
              }
            />
            <ToggleRow
              id="wizard-images"
              title={t("showProductImagesOnCustomerMenu")}
              hint={t("showProductImagesOnCustomerMenuHint")}
              kind="images"
              previewPage="menu"
              onPreviewPage={setLivePreviewPage}
              on={imagesShown}
              onChange={(shown) => patchMenu({ productImagesDisabled: !shown })}
            />
            <ToggleRow
              id="wizard-prices"
              title={t("showProductPricesOnCustomerMenu")}
              hint={t("showProductPricesOnCustomerMenuHint")}
              kind="prices"
              previewPage="menu"
              onPreviewPage={setLivePreviewPage}
              on={pricesShown}
              onChange={(shown) => patchMenu({ productPricesHidden: !shown })}
            />
            <ToggleRow
              id="wizard-search"
              title={t("showSearchBarOnCustomerMenu")}
              hint={t("showSearchBarOnCustomerMenuHint")}
              kind="search"
              previewPage="menu"
              onPreviewPage={setLivePreviewPage}
              on={searchShown}
              onChange={(shown) => patchMenu({ searchBarHidden: !shown })}
            />
            <ToggleRow
              id="wizard-summary"
              title={t("summaryButtonOnCustomerMenu")}
              hint={t("summaryButtonOnCustomerMenuHint")}
              kind="summary"
              previewPage="menu"
              onPreviewPage={setLivePreviewPage}
              on={summaryEnabled}
              onChange={(summaryButtonEnabled) => patchMenu({ summaryButtonEnabled })}
            />
          </div>
          <ClientMenuLivePreview
            restaurantData={restaurant}
            menuDisplay={menu}
            accent={ACCENT}
            focusPage={livePreviewPage}
          />
        </div>
      ) : (
        <div className="start-visual-live start-visual-live--recommended">
          <ClientMenuLivePreview
            restaurantData={restaurant}
            menuDisplay={menu}
            accent={ACCENT}
            focusPage={livePreviewPage}
          />
        </div>
      )}
    </div>
  );
}
